// Client-side "demo mode" data for the guided tutorial.
//
// While the tutorial is engaged AND the tenant has little real analytics data
// (see TutorialContext), the four analytics read endpoints in
// ``analyticsService`` return these curated, deterministic payloads instead of
// hitting the network. This lets a brand-new pilot tenant SEE every analytics
// page working with rich, realistic data — without ever mutating the live
// database. When the tutorial ends the flag is cleared and the analytics client
// cache is dropped, so real data returns immediately.
//
// IMPORTANT: this module must NOT import from analyticsService (that service
// imports from here) to avoid a circular dependency.

import type {
  GamificationProfile,
  GeoAnalytics,
  AiAnalytics,
  ReportSummary,
  Badge,
  Quest,
  LeaderboardEntry,
  CommunityGoal,
  GeoPoint,
} from '../types/analytics';

// --------------------------------------------------------------------------- //
// Demo-mode flag (module-local, in-memory only — never persisted).
// --------------------------------------------------------------------------- //

let demoActive = false;

export function setAnalyticsDemoActive(active: boolean): void {
  demoActive = active;
}

export function isAnalyticsDemoActive(): boolean {
  return demoActive;
}

// --------------------------------------------------------------------------- //
// Small date helpers so trend/seasonality data always looks current.
// --------------------------------------------------------------------------- //

/** Returns the last ``n`` month labels as 'YYYY-MM', oldest first. */
function recentMonths(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/** ISO date string for ``n`` days before now. */
function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

// --------------------------------------------------------------------------- //
// Gamification (Stats) page
// --------------------------------------------------------------------------- //

export function buildDemoGamificationProfile(): GamificationProfile {
  const badges: Badge[] = [
    { id: 'first-report', name: 'First Report', description: 'Logged your first hazard', icon: 'target', earned: true, tier: 'bronze', threshold: 1, value: 38, progress: 1, bonus: 25 },
    { id: 'resolver', name: 'Resolver', description: 'Resolved 25 hazards', icon: 'check', earned: true, tier: 'silver', threshold: 25, value: 25, progress: 1, bonus: 100 },
    { id: 'streak-7', name: 'On a Roll', description: 'Reported 7 days in a row', icon: 'fire', earned: false, tier: 'gold', threshold: 7, value: 6, progress: 0.86, bonus: 150 },
    { id: 'community-hero', name: 'Community Hero', description: 'Reached the top 5 leaderboard', icon: 'trophy', earned: true, tier: 'gold', threshold: 5, value: 4, progress: 1, bonus: 200 },
  ];

  const quests: Quest[] = [
    { id: 'q-report', label: 'Report 3 hazards today', metric: 'reports', icon: 'clipboard', target: 3, bonus: 30, value: 2, earned: false, progress: 0.66 },
    { id: 'q-resolve', label: 'Resolve 1 open hazard', metric: 'resolutions', icon: 'check', target: 1, bonus: 20, value: 1, earned: true, progress: 1 },
    { id: 'q-comment', label: 'Add 2 helpful comments', metric: 'comments', icon: 'star', target: 2, bonus: 15, value: 1, earned: false, progress: 0.5 },
  ];

  const leaderboard: LeaderboardEntry[] = [
    { userId: 'u-amara', name: 'Amara Okafor', profileImageUrl: null, reports: 84, resolutions: 71, points: 5120, rank: 1 },
    { userId: 'u-liam', name: 'Liam Chen', profileImageUrl: null, reports: 76, resolutions: 60, points: 4380, rank: 2 },
    { userId: 'u-sofia', name: 'Sofia Rossi', profileImageUrl: null, reports: 61, resolutions: 48, points: 3410, rank: 3 },
    { userId: 'demo-user', name: 'You', profileImageUrl: null, reports: 38, resolutions: 25, points: 2860, rank: 4 },
    { userId: 'u-noah', name: 'Noah Williams', profileImageUrl: null, reports: 33, resolutions: 20, points: 2240, rank: 5 },
    { userId: 'u-priya', name: 'Priya Patel', profileImageUrl: null, reports: 27, resolutions: 16, points: 1780, rank: 6 },
  ];

  const goals: CommunityGoal[] = [
    { id: 'g-reports', label: 'Monthly reports', current: 96, target: 120, progress: 0.8 },
    { id: 'g-resolutions', label: 'Monthly resolutions', current: 74, target: 90, progress: 0.82 },
  ];

  return {
    community: {
      hazardsReported: 1284,
      hazardsResolved: 1057,
      resolutionRate: 82,
      totalContributors: 47,
    },
    me: {
      userId: 'demo-user',
      name: 'You',
      reports: 38,
      resolutions: 25,
      comments: 61,
      reactions: 143,
      points: 2860,
      bonusPoints: 400,
      rank: 4,
      totalContributors: 47,
      level: {
        level: 7,
        title: 'Safety Captain',
        pointsIntoLevel: 360,
        pointsForNextLevel: 600,
        progress: 0.6,
        totalPoints: 2860,
      },
      streak: { current: 6, longest: 14 },
      badges,
      quests,
    },
    leaderboard,
    goals,
    settings: {
      activeWeekdays: [0, 1, 2, 3, 4],
      monthlyReportsTarget: 120,
      monthlyResolutionsTarget: 90,
    },
    canEditSettings: false,
  };
}

// --------------------------------------------------------------------------- //
// Geo page
// --------------------------------------------------------------------------- //

export function buildDemoGeoAnalytics(): GeoAnalytics {
  const seed: Array<[string, string, number, number, number, string]> = [
    // city, province, lat, lon, severity(1-4), severityLabel
    ['Calgary', 'AB', 51.0447, -114.0719, 4, 'Extreme'],
    ['Edmonton', 'AB', 53.5461, -113.4938, 3, 'High'],
    ['Vancouver', 'BC', 49.2827, -123.1207, 3, 'High'],
    ['Victoria', 'BC', 48.4284, -123.3656, 2, 'Medium'],
    ['Toronto', 'ON', 43.6532, -79.3832, 3, 'High'],
    ['Ottawa', 'ON', 45.4215, -75.6972, 2, 'Medium'],
    ['Montreal', 'QC', 45.5019, -73.5674, 2, 'Medium'],
    ['Yellowknife', 'NT', 62.454, -114.3718, 4, 'Extreme'],
    ['Fort McMurray', 'AB', 56.7268, -111.379, 4, 'Extreme'],
    ['Winnipeg', 'MB', 49.8951, -97.1384, 2, 'Medium'],
  ];

  const points: GeoPoint[] = seed.map(([city, province, lat, lon, severity, severityLabel]) => ({
    lat,
    lon,
    eventType: severity >= 4 ? 'Fire/Heat' : severity >= 3 ? 'Slip/Trip/Fall' : 'Struck-by',
    city,
    province,
    severity,
    severityLabel,
    weight: severity,
  }));

  return {
    points,
    byProvince: [
      { province: 'AB', count: 412, resolved: 351, resolutionRate: 85, topHazard: 'Slip/Ice' },
      { province: 'BC', count: 286, resolved: 224, resolutionRate: 78, topHazard: 'Electrical' },
      { province: 'ON', count: 341, resolved: 289, resolutionRate: 85, topHazard: 'Fall from Height' },
      { province: 'QC', count: 138, resolved: 108, resolutionRate: 78, topHazard: 'Struck-by' },
      { province: 'MB', count: 61, resolved: 49, resolutionRate: 80, topHazard: 'Cold Stress' },
      { province: 'NT', count: 46, resolved: 36, resolutionRate: 78, topHazard: 'Wildlife' },
    ],
    byCity: [
      { city: 'Calgary', province: 'AB', count: 214, lat: 51.0447, lon: -114.0719 },
      { city: 'Toronto', province: 'ON', count: 187, lat: 43.6532, lon: -79.3832 },
      { city: 'Vancouver', province: 'BC', count: 163, lat: 49.2827, lon: -123.1207 },
      { city: 'Edmonton', province: 'AB', count: 121, lat: 53.5461, lon: -113.4938 },
      { city: 'Montreal', province: 'QC', count: 98, lat: 45.5019, lon: -73.5674 },
      { city: 'Ottawa', province: 'ON', count: 77, lat: 45.4215, lon: -75.6972 },
    ],
    severityByProvince: [
      { province: 'AB', low: 96, medium: 158, high: 112, extreme: 46, severityIndex: 2.3 },
      { province: 'BC', low: 71, medium: 118, high: 74, extreme: 23, severityIndex: 2.2 },
      { province: 'ON', low: 88, medium: 141, high: 89, extreme: 23, severityIndex: 2.1 },
      { province: 'QC', low: 41, medium: 59, high: 31, extreme: 7, severityIndex: 2.0 },
      { province: 'NT', low: 6, medium: 14, high: 15, extreme: 11, severityIndex: 2.7 },
    ],
    seasonality: recentMonths(8).map((period, i) => ({
      period,
      count: [78, 92, 110, 134, 121, 145, 138, 156][i] ?? 100,
    })),
    totals: {
      mappedIncidents: 1284,
      provinces: 6,
      cities: 34,
    },
  };
}

// --------------------------------------------------------------------------- //
// AI page
// --------------------------------------------------------------------------- //

export function buildDemoAiAnalytics(): AiAnalytics {
  const months = recentMonths(6);
  return {
    sentiment: {
      count: 1284,
      avgPolarity: -0.18,
      avgSubjectivity: 0.42,
      distribution: {
        positive: { count: 232, pct: 18 },
        neutral: { count: 604, pct: 47 },
        negative: { count: 448, pct: 35 },
      },
    },
    sentimentTrend: months.map((period, i) => ({
      period,
      avgPolarity: [-0.24, -0.21, -0.19, -0.16, -0.2, -0.15][i] ?? -0.18,
      count: [186, 201, 214, 233, 220, 230][i] ?? 200,
    })),
    risk: {
      count: 1284,
      overallRisk: 6.4,
      avgRisk: 4.9,
      distribution: {
        low: { count: 372, pct: 29 },
        medium: { count: 528, pct: 41 },
        high: { count: 268, pct: 21 },
        extreme: { count: 116, pct: 9 },
      },
    },
    riskTrend: months.map((period, i) => ({
      period,
      overallRisk: [5.9, 6.1, 6.3, 6.6, 6.2, 6.5][i] ?? 6.3,
      avgRisk: [4.6, 4.7, 4.9, 5.1, 4.8, 5.0][i] ?? 4.9,
      count: [186, 201, 214, 233, 220, 230][i] ?? 200,
    })),
    topHazards: [
      { term: 'slip', category: 'Slip/Trip/Fall', count: 214, avgRisk: 5.2 },
      { term: 'ice', category: 'Environmental', count: 188, avgRisk: 5.6 },
      { term: 'electrical', category: 'Electrical', count: 141, avgRisk: 7.1 },
      { term: 'fall', category: 'Fall from Height', count: 133, avgRisk: 6.8 },
      { term: 'heat stress', category: 'Fire/Heat', count: 97, avgRisk: 6.4 },
      { term: 'wild life', category: 'Wildlife', count: 74, avgRisk: 5.0 },
    ],
    topTags: [
      { tag: 'slip', count: 214 },
      { tag: 'ice', count: 188 },
      { tag: 'electrical', count: 141 },
      { tag: 'fall', count: 133 },
      { tag: 'heat stress', count: 97 },
      { tag: 'wild life', count: 74 },
      { tag: 'struck', count: 63 },
    ],
    topKeywords: [
      { token: 'parking lot', count: 132 },
      { token: 'walkway', count: 118 },
      { token: 'scaffold', count: 96 },
      { token: 'loading dock', count: 88 },
      { token: 'ventilation', count: 61 },
    ],
    categories: [
      { eventType: 'Slip/Trip/Fall', count: 402 },
      { eventType: 'Electrical', count: 214 },
      { eventType: 'Fall from Height', count: 198 },
      { eventType: 'Fire/Heat', count: 166 },
      { eventType: 'Struck-by / Vehicle', count: 141 },
      { eventType: 'Wildlife Encounter', count: 92 },
    ],
    notable: {
      mostNegative: [
        { id: 'demo-n1', description: 'Frayed cable in a damp utility room created an electrical hazard.', polarity: -0.72, city: 'Vancouver', province: 'BC', eventType: 'Electrical' },
        { id: 'demo-n2', description: 'Compact snow and ice on the stairs created a serious slip hazard.', polarity: -0.61, city: 'Calgary', province: 'AB', eventType: 'Slip/Trip/Fall' },
      ],
      mostPositive: [
        { id: 'demo-p1', description: 'New anti-slip matting installed at the entrance, with no incidents since.', polarity: 0.58, city: 'Toronto', province: 'ON', eventType: 'Slip/Trip/Fall' },
      ],
      highestRisk: [
        { id: 'demo-r1', description: 'Wet electrical panel cover removed; risk of shock flagged immediately.', riskScore: 9.1, severityLevel: 'Extreme', consequenceType: 'Electrocution', city: 'Victoria', province: 'BC', eventType: 'Electrical' },
        { id: 'demo-r2', description: 'Spot fire flared up near the dry brush bordering the laydown yard.', riskScore: 8.7, severityLevel: 'Extreme', consequenceType: 'Burn', city: 'Fort McMurray', province: 'AB', eventType: 'Fire/Heat' },
      ],
    },
    available: {
      sentiment: true,
      risk: true,
      tokens: true,
      tags: true,
    },
  };
}

// --------------------------------------------------------------------------- //
// Reports page
// --------------------------------------------------------------------------- //

export function buildDemoReportSummary(): ReportSummary {
  const months = recentMonths(6);
  return {
    period: { start: isoDaysAgo(180), end: new Date().toISOString() },
    headline: {
      reported: 1284,
      resolved: 1057,
      resolutionRate: 82,
      activeUsers: 47,
      avgPolarity: -0.18,
      overallRisk: 6.4,
    },
    trend: months.map((period, i) => ({
      period,
      reported: [186, 201, 214, 233, 220, 230][i] ?? 200,
      resolved: [150, 168, 179, 196, 182, 182][i] ?? 170,
    })),
    severityBreakdown: {
      counts: { low: 372, medium: 528, high: 268, extreme: 116 },
      distribution: {
        low: { count: 372, pct: 29 },
        medium: { count: 528, pct: 41 },
        high: { count: 268, pct: 21 },
        extreme: { count: 116, pct: 9 },
      },
      severityIndex: 2.2,
    },
    oshaEventExposure: [
      { label: 'Falls, Slips, Trips', count: 402, pct: 31 },
      { label: 'Exposure to Electricity', count: 214, pct: 17 },
      { label: 'Fall to Lower Level', count: 198, pct: 15 },
      { label: 'Fires and Explosions', count: 166, pct: 13 },
      { label: 'Contact with Object or Equipment', count: 141, pct: 11 },
    ],
    topRegions: [
      { province: 'AB', count: 412, pct: 32 },
      { province: 'ON', count: 341, pct: 27 },
      { province: 'BC', count: 286, pct: 22 },
      { province: 'QC', count: 138, pct: 11 },
      { province: 'MB', count: 61, pct: 5 },
    ],
    topTags: [
      { tag: 'slip', count: 214 },
      { tag: 'ice', count: 188 },
      { tag: 'electrical', count: 141 },
      { tag: 'fall', count: 133 },
      { tag: 'heat stress', count: 97 },
    ],
    riskHighlights: [
      { province: 'NT', overallRisk: 7.4, avgRisk: 5.5, count: 46 },
      { province: 'BC', overallRisk: 6.9, avgRisk: 5.1, count: 286 },
      { province: 'AB', overallRisk: 6.6, avgRisk: 5.0, count: 412 },
    ],
    recentNotable: [
      { id: 'demo-rn1', description: 'Wet electrical panel cover removed; risk of shock flagged immediately.', city: 'Victoria', province: 'BC', eventType: 'Electrical', date: isoDaysAgo(2), severity: 4, severityLabel: 'Extreme' },
      { id: 'demo-rn2', description: 'Unguarded floor opening on the 4th level of the high-rise build.', city: 'Toronto', province: 'ON', eventType: 'Fall from Height', date: isoDaysAgo(4), severity: 3, severityLabel: 'High' },
      { id: 'demo-rn3', description: 'Slipped on black ice in the parking lot near the entrance.', city: 'Calgary', province: 'AB', eventType: 'Slip/Trip/Fall', date: isoDaysAgo(6), severity: 3, severityLabel: 'High' },
    ],
    generatedAt: new Date().toISOString(),
  };
}
